import { useCallback, useMemo, useState } from 'react'
import { writeTrackers } from '../data/trackerStore'
import {
  TRACKER_COLORS,
  newTrackerId,
  trackersProblem,
  type Tracker,
} from '../data/trackers'
import { sameValue } from '../lib/equal'
import { SaveStatus } from './SaveStatus'
import { useAutoSave } from './useAutoSave'

interface Props {
  uid: string
  trackers: Tracker[]
}

/** Whatever you want to keep a count of. Saves itself, like the taxonomy. */
export function TrackersEditor({ uid, trackers }: Props) {
  const [draft, setDraft] = useState(trackers)
  const dirty = useMemo(() => !sameValue(draft, trackers), [draft, trackers])
  const write = useCallback((next: Tracker[]) => writeTrackers(uid, next), [uid])
  const status = useAutoSave(draft, dirty, write, { validate: trackersProblem })

  function patch(index: number, change: Partial<Tracker>) {
    setDraft((current) =>
      current.map((tracker, i) => (i === index ? { ...tracker, ...change } : tracker)),
    )
  }

  function add() {
    setDraft((current) => [
      ...current,
      {
        id: newTrackerId('tracker', new Set(current.map((tracker) => tracker.id))),
        label: '',
        kind: 'count',
        unit: null,
        color: TRACKER_COLORS[current.length % TRACKER_COLORS.length] as string,
        archived: false,
      },
    ])
  }

  return (
    <div className="flex flex-col gap-3 p-3">
      <p className="text-[12px] leading-relaxed text-faint">
        A <strong className="font-medium text-dim">count</strong> asks how many — cups of coffee,
        trips to the office. A <strong className="font-medium text-dim">yes/no</strong> just asks
        whether it happened. Either way what you get back is when, not a score. Changes save
        themselves a moment after you stop typing.
      </p>

      <ul className="flex flex-col gap-2">
        {draft.map((tracker, index) => (
          <li key={tracker.id} className="flex flex-wrap items-center gap-2">
            <label className="relative size-7 shrink-0 overflow-hidden rounded-md border border-line-strong">
              <span className="absolute inset-0" style={{ backgroundColor: tracker.color }} />
              <input
                type="color"
                value={tracker.color}
                onChange={(event) => patch(index, { color: event.target.value })}
                aria-label={`Colour for ${tracker.label || 'tracker'}`}
                className="absolute inset-0 cursor-pointer opacity-0"
              />
            </label>

            <input
              value={tracker.label}
              onChange={(event) => patch(index, { label: event.target.value })}
              aria-label="Tracker name"
              placeholder="Coffee"
              className="min-w-0 flex-1 basis-40 rounded-md border border-line bg-bg px-2.5 py-1.5 text-[14px] outline-none focus:border-line-strong sm:max-w-xs"
            />

            <div className="flex shrink-0 rounded-lg border border-line p-0.5">
              {(['count', 'check'] as const).map((kind) => (
                <button
                  key={kind}
                  type="button"
                  onClick={() => patch(index, { kind })}
                  aria-pressed={tracker.kind === kind}
                  className={`rounded-md px-2.5 py-1 text-[13px] font-medium transition-colors duration-150 ${
                    tracker.kind === kind ? 'bg-raised text-text' : 'text-dim hover:text-text'
                  }`}
                >
                  {kind === 'count' ? 'Count' : 'Yes/no'}
                </button>
              ))}
            </div>

            {tracker.kind === 'count' ? (
              <input
                value={tracker.unit ?? ''}
                onChange={(event) => patch(index, { unit: event.target.value || null })}
                aria-label="Unit"
                placeholder="cup"
                title="What one of them is called"
                className="w-20 shrink-0 rounded-md border border-line bg-bg px-2 py-1.5 text-[13px] outline-none focus:border-line-strong"
              />
            ) : null}

            <button
              type="button"
              onClick={() => patch(index, { archived: !tracker.archived })}
              aria-pressed={tracker.archived}
              title={tracker.archived ? 'Bring it back' : 'Retire it, keeping its history'}
              className={`shrink-0 rounded-md border px-2 py-1.5 text-[12px] transition-colors duration-150 ${
                tracker.archived
                  ? 'border-line-strong bg-raised text-text'
                  : 'border-line text-faint hover:text-dim'
              }`}
            >
              {tracker.archived ? 'Retired' : 'Retire'}
            </button>
          </li>
        ))}
      </ul>

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={add}
          className="rounded-lg border border-line-strong px-3 py-1.5 text-[14px] font-medium text-dim transition-colors duration-150 hover:text-text"
        >
          Add a tracker
        </button>
        <span className="ml-auto text-right" role="status" aria-live="polite">
          <SaveStatus state={status.state} message={status.message} />
        </span>
      </div>
    </div>
  )
}
